import { useState } from "react";
import { EyeIcon } from "@heroicons/react/outline";
import TruncatedText from "components/TruncatedText";
import StatusBadge from "pages/crawler/components/jobs/StatusBadge";
import ViewJobResponseModal from "components/ViewJobResponseModal";
import scraperApi from "api";
import Toaster from "components/Toaster";
import { formatTimeAgo } from "utils/dateUtils";
import { isStatusCompleted } from "pages/crawler/utils/crawlerStatusUtils";
import { DataTable, Column } from "components/Table";
import { EmptyUrlsState } from "components/UrlsTableStates";
import type { JobUrl } from "pages/crawler/components/jobs/types";

export const JobUrlsTable = ({
  urls,
  hasActiveFilters = false,
}: {
  urls: JobUrl[];
  hasActiveFilters?: boolean;
}) => {
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [selectedUrlData, setSelectedUrlData] = useState<any>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const handleViewResponse = async (urlData: JobUrl) => {
    // Check if this URL has a result key
    if (!urlData.resultKey) {
      Toaster.error("No results available for this URL");
      return;
    }

    setLoadingPreview(true);
    try {
      // Fetch the results for this specific URL using its result key
      const response = await scraperApi.crawlers.getCrawlerResultsByKey(
        urlData.resultKey,
      );

      setSelectedUrlData({
        id: urlData.id,
        url: urlData.url,
        response: response,
      });
      setPreviewModalOpen(true);
    } catch (error: any) {
      console.error("Failed to fetch URL response:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to load response data";
      Toaster.error(errorMessage);
    } finally {
      setLoadingPreview(false);
    }
  };

  const viewJobResponseOnClose = () => {
    setPreviewModalOpen(false);
    setSelectedUrlData(null);
  };

  const columns: Column<JobUrl>[] = [
    {
      key: "url",
      header: "URL",
      tooltip: "Scraped URL",
      cell: (urlData) => <TruncatedText text={urlData.url} />,
      cellClassName: "whitespace-nowrap text-gray-900",
    },
    {
      key: "crawled",
      header: "Crawled",
      tooltip: "When this URL was last crawled",
      cell: (urlData) => formatTimeAgo(urlData.crawledAt, { fallback: "Never" }),
      cellClassName: "whitespace-nowrap text-gray-500",
    },
    {
      key: "status",
      header: "Status",
      tooltip: "Current crawl status",
      cell: (urlData) => <StatusBadge status={urlData.status} />,
      cellClassName: "whitespace-nowrap",
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      cell: (urlData) =>
        urlData.resultKey && isStatusCompleted(urlData.status) ? (
          <button
            onClick={() => handleViewResponse(urlData)}
            disabled={loadingPreview}
            className="inline-flex items-center justify-center p-1 text-gray-600 hover:text-rebrandBlue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="View response"
          >
            <EyeIcon className="h-5 w-5" />
          </button>
        ) : null,
      cellClassName: "whitespace-nowrap font-medium",
    },
  ];

  return (
    <>
      <ViewJobResponseModal
        isOpen={previewModalOpen}
        onClose={viewJobResponseOnClose}
        urlData={selectedUrlData}
      />

      <DataTable
        columns={columns}
        rows={urls}
        getRowKey={(urlData, index) => urlData.id || `url-${index}-${urlData.url}`}
        empty={<EmptyUrlsState hasActiveFilters={hasActiveFilters} />}
      />
    </>
  );
};

export default JobUrlsTable;
